import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API_BASE = `${BACKEND_URL}/api`;

// Authenticated axios instance (admin JWT)
const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  // Attach Bearer token from localStorage (needed for cross-origin deployments)
  const token = localStorage.getItem('entricode_token');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  // Read CSRF token from cookie (not httpOnly, readable by JS)
  const csrf = document.cookie
    .split('; ')
    .find(row => row.startsWith('entricode_csrf='))
    ?.split('=')[1];
  if (csrf) config.headers['X-CSRF-Token'] = csrf;
  return config;
});

let _isRefreshing = false;
let _refreshQueue = [];

function _processQueue(error, success = false) {
  _refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  _refreshQueue = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (_isRefreshing) {
        return new Promise((resolve, reject) => {
          _refreshQueue.push({ resolve, reject });
        }).then(() => {
          originalRequest._retry = true;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      _isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('entricode_refresh_token');
        const refreshRes = await axios.post(`${API_BASE}/auth/refresh`, null, {
          withCredentials: true,
          headers: refreshToken ? { 'Authorization': `Bearer ${refreshToken}` } : {},
        });
        if (refreshRes.data?.access_token) {
          localStorage.setItem('entricode_token', refreshRes.data.access_token);
        }
        if (refreshRes.data?.refresh_token) {
          localStorage.setItem('entricode_refresh_token', refreshRes.data.refresh_token);
        }
        _processQueue(null, true);
        return api(originalRequest);
      } catch (refreshError) {
        _processQueue(refreshError);
        localStorage.removeItem('entricode_admin');
        localStorage.removeItem('entricode_token');
        localStorage.removeItem('entricode_refresh_token');
        window.location.href = '/admin/login';
        return Promise.reject(refreshError);
      } finally {
        _isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ── Auth ──────────────────────────────────────────────────────
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (email, password, name) => api.post('/auth/register', { email, password, name }),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// ── Admin Settings ────────────────────────────────────────────
export const adminAPI = {
  getSmtp: () => api.get('/admin/smtp'),
  updateSmtp: (data) => api.put('/admin/smtp', data),
  testSmtp: () => api.post('/admin/smtp/test'),
  updateProfile: (data) => api.put('/admin/profile', data),
};

// ── Events ────────────────────────────────────────────────────
export const eventsAPI = {
  list: () => api.get('/events'),
  get: (id) => api.get(`/events/${id}`),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
  close: (id) => api.post(`/events/${id}/close`),
  getReport: (id) => api.get(`/events/${id}/report`),
  getReportXlsx: (id) => api.get(`/events/${id}/report/xlsx`, { responseType: 'blob' }),
  getAnalytics: (id) => api.get(`/events/${id}/analytics`),
  uploadImage: (id, imageType, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/events/${id}/upload-image?image_type=${imageType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getSmtp: (id) => api.get(`/events/${id}/smtp`),
  updateSmtp: (id, data) => api.put(`/events/${id}/smtp`, data),
  testSmtp: (id) => api.post(`/events/${id}/smtp/test`),
  createDemoToken: (id) => api.post(`/events/${id}/demo-token`),
};

// ── Attendees ─────────────────────────────────────────────────
export const attendeesAPI = {
  list: (eventId, params = {}) => api.get(`/events/${eventId}/attendees`, { params }),
  create: (eventId, data) => api.post(`/events/${eventId}/attendees`, data),
  invalidate: (eventId, attendeeId) => api.put(`/events/${eventId}/attendees/${attendeeId}/invalidate`),
  toggleValidate: (eventId, attendeeId) => api.put(`/events/${eventId}/attendees/${attendeeId}/validate`),
  update: (eventId, attendeeId, data) => api.patch(`/events/${eventId}/attendees/${attendeeId}`, data),
  resendEmail: (eventId, attendeeId) => api.post(`/events/${eventId}/attendees/${attendeeId}/resend-email`),
  getQR: (eventId, attendeeId) => api.get(`/events/${eventId}/attendees/${attendeeId}/qr`),
  import: (eventId, file, sendEmails = false) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/events/${eventId}/import?send_emails=${sendEmails}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  export: (eventId) => api.get(`/events/${eventId}/export`, { responseType: 'blob' }),
  getImportTemplate: (eventId) => api.get(`/events/${eventId}/import-template`, { responseType: 'blob' }),
  deleteAll: (eventId) => api.delete(`/events/${eventId}/attendees?confirm=DELETE_ALL`),
};

// ── Public (no auth) ──────────────────────────────────────────
export const publicAPI = {
  getEvent: (slug) => axios.get(`${API_BASE}/public/events/${slug}`),
  register: (slug, data) => axios.post(`${API_BASE}/public/events/${slug}/register`, data),
};

// ── Staff (code-authenticated, no JWT) ────────────────────────
export const staffAPI = {
  getTokens: (eventId, code) => axios.get(`${API_BASE}/staff/events/${eventId}/tokens`, { params: { code } }),
  validate: (token, eventId, code) => axios.post(`${API_BASE}/staff/validate`, { token, event_id: eventId, code }),
  access: (data) => axios.post(`${API_BASE}/staff/access`, data),
  badge: (token, eventId, code) => axios.post(`${API_BASE}/staff/badge`, { token, event_id: eventId, code }),
  sync: (eventId, queue, code) => axios.post(`${API_BASE}/staff/sync`, { event_id: eventId, queue, code }),
  getPrintConfig: (eventId, code) => axios.get(`${API_BASE}/staff/events/${eventId}/print-config`, { params: { code } }),
  generateZPL: (data) => axios.post(`${API_BASE}/staff/generate-zpl`, data),
  generateTestQrZpl: (data) => axios.post(`${API_BASE}/staff/generate-test-qr-zpl`, data),
  networkPrint: (printerIP, printerPort, zpl, eventId, code) => axios.post(`${API_BASE}/staff/network-print`, { printer_ip: printerIP, printer_port: printerPort, zpl, event_id: eventId || '', code: code || '' }),
  networkPrintTest: (printerIP, printerPort, zpl, eventId, code) => axios.post(`${API_BASE}/staff/network-print-test`, { printer_ip: printerIP, printer_port: printerPort || 9100, zpl: zpl || '', event_id: eventId || '', code: code || '' }),
  searchAttendees: (eventId, search, code) => axios.get(`${API_BASE}/staff/events/${eventId}/attendee-list`, { params: { search, code } }),
  validateRoomAccess: (token, eventId, roomId, code) => axios.post(`${API_BASE}/staff/validate-room-access`, { token, event_id: eventId, room_id: roomId, code }),
  getRooms: (eventId, code) => axios.get(`${API_BASE}/staff/events/${eventId}/rooms`, { params: { code } }),
  syncWalkin: (data) => axios.post(`${API_BASE}/staff/sync-walkin`, data),
};

// ── Venues (rooms, sessions, pass types) ──────────────────────
export const roomsAPI = {
  list: (eventId) => api.get(`/events/${eventId}/rooms`),
  create: (eventId, data) => api.post(`/events/${eventId}/rooms`, data),
  update: (eventId, roomId, data) => api.put(`/events/${eventId}/rooms/${roomId}`, data),
  delete: (eventId, roomId) => api.delete(`/events/${eventId}/rooms/${roomId}`),
};

export const sessionsAPI = {
  list: (eventId) => api.get(`/events/${eventId}/sessions`),
  create: (eventId, data) => api.post(`/events/${eventId}/sessions`, data),
  update: (eventId, sessionId, data) => api.put(`/events/${eventId}/sessions/${sessionId}`, data),
  delete: (eventId, sessionId) => api.delete(`/events/${eventId}/sessions/${sessionId}`),
};

export const passTypesAPI = {
  list: (eventId) => api.get(`/events/${eventId}/pass-types`),
  create: (eventId, data) => api.post(`/events/${eventId}/pass-types`, data),
  update: (eventId, passId, data) => api.put(`/events/${eventId}/pass-types/${passId}`, data),
  delete: (eventId, passId) => api.delete(`/events/${eventId}/pass-types/${passId}`),
};

// ── Sponsors ──────────────────────────────────────────────────
export const sponsorsAPI = {
  list: (eventId) => api.get(`/events/${eventId}/sponsors`),
  add: (eventId, formData) => api.post(`/events/${eventId}/sponsors`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (eventId, sponsorId) => api.delete(`/events/${eventId}/sponsors/${sponsorId}`),
};

// ── Payments ──────────────────────────────────────────────────
export const paymentsAPI = {
  createCheckout: (eventId, attendeeId) => {
    return axios.post(`${API_BASE}/payments/checkout`, {
      event_id: eventId,
      attendee_id: attendeeId,
      origin_url: window.location.origin,
    });
  },
  getStatus: (sessionId) => axios.get(`${API_BASE}/payments/status/${sessionId}`),
};

// ── Badge Templates ───────────────────────────────────────────
export const badgeAPI = {
  getTemplates: (eventId) => api.get(`/events/${eventId}/badge-templates`),
  createTemplate: (eventId, data) => api.post(`/events/${eventId}/badge-templates`, data),
  updateTemplate: (eventId, templateId, data) => api.put(`/events/${eventId}/badge-templates/${templateId}`, data),
  deleteTemplate: (eventId, templateId) => api.delete(`/events/${eventId}/badge-templates/${templateId}`),
  duplicateTemplate: (eventId, templateId) => api.post(`/events/${eventId}/badge-templates/${templateId}/duplicate`),
  previewZPL: (eventId, templateId) => api.post(`/events/${eventId}/badge-templates/${templateId}/preview-zpl`),
};

// ── Print Stations ────────────────────────────────────────────
export const stationsAPI = {
  list: (eventId) => api.get(`/events/${eventId}/print-stations`),
  create: (eventId, data) => api.post(`/events/${eventId}/print-stations`, data),
  update: (eventId, stationId, data) => api.put(`/events/${eventId}/print-stations/${stationId}`, data),
  delete: (eventId, stationId) => api.delete(`/events/${eventId}/print-stations/${stationId}`),
};

// ── Email Logs ────────────────────────────────────────────────
export const emailAPI = {
  getLogs: (eventId) => api.get(`/events/${eventId}/email-logs`),
  testSmtp: (eventId) => api.post(`/events/${eventId}/test-smtp`),
};

// ── AI Assistant ──────────────────────────────────────────────
export const aiAPI = {
  getSettings: (eventId) => api.get(`/events/${eventId}/ai/settings`),
  updateSettings: (eventId, data) => api.put(`/events/${eventId}/ai/settings`, data),
  getDocuments: (eventId) => api.get(`/events/${eventId}/ai/documents`),
  uploadDocument: (eventId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/events/${eventId}/ai/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteDocument: (eventId, docId) => api.delete(`/events/${eventId}/ai/documents/${docId}`),
  uploadAvatar: (eventId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/events/${eventId}/ai/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteAvatar: (eventId) => api.delete(`/events/${eventId}/ai/avatar`),
  getStatus: (eventId) => axios.get(`${API_BASE}/events/${eventId}/ai/status`),
  chat: (eventId, message, sessionId) => axios.post(`${API_BASE}/events/${eventId}/ai/chat`, { message, session_id: sessionId }),
};

// ── Exhibitors (admin) ────────────────────────────────────────
export const exhibitorsAPI = {
  list: (eventId) => api.get(`/exhibitors/events/${eventId}/exhibitors`),
  create: (eventId, data) => api.post(`/exhibitors/events/${eventId}/exhibitors`, data),
  update: (eventId, exhibitorId, data) => api.put(`/exhibitors/events/${eventId}/exhibitors/${exhibitorId}`, data),
  delete: (eventId, exhibitorId) => api.delete(`/exhibitors/events/${eventId}/exhibitors/${exhibitorId}`),
  getLeads: (eventId, exhibitorId) => api.get(`/exhibitors/events/${eventId}/exhibitors/${exhibitorId}/leads`),
  listStandTypes: (eventId) => api.get(`/exhibitors/events/${eventId}/stand-types`),
};

// ── Exhibitor App (token-based, no JWT) ───────────────────────
export const exhibitorAppAPI = {
  getProfile: (token) => axios.get(`${API_BASE}/exhibitors/app/${token}/profile`),
  updateProfile: (token, data) => axios.put(`${API_BASE}/exhibitors/app/${token}/profile`, data),
  scan: (token, data) => axios.post(`${API_BASE}/exhibitors/app/${token}/scan`, data),
  getLeads: (token) => axios.get(`${API_BASE}/exhibitors/app/${token}/leads`),
  updateLead: (token, leadId, data) => axios.put(`${API_BASE}/exhibitors/app/${token}/leads/${leadId}`, data),
  deleteLead: (token, leadId) => axios.delete(`${API_BASE}/exhibitors/app/${token}/leads/${leadId}`),
  exportLeads: (token) => `${API_BASE}/exhibitors/app/${token}/export`,
  register: (slug, data) => axios.post(`${API_BASE}/exhibitors/public/${slug}/exhibitor-register`, data),
  getLeadCaptureStatus: (token) => axios.get(`${API_BASE}/exhibitors/app/${token}/lead-capture-status`),
  getLeadCaptureText: (token) => axios.get(`${API_BASE}/exhibitors/app/${token}/lead-capture-text`),
  acceptLeadCapture: (token) => axios.post(`${API_BASE}/exhibitors/app/${token}/accept-lead-capture`, { accept_lead_capture: true }),
  getOfflineData: (token) => axios.get(`${API_BASE}/exhibitors/app/${token}/offline-data`),
  syncLeads: (token, leads) => axios.post(`${API_BASE}/exhibitors/app/${token}/sync-leads`, { leads }),
  // Portal (exhibitor staff management)
  getStaff: (token) => axios.get(`${API_BASE}/exhibitors/portal/${token}/staff`),
  createStaff: (token, data) => axios.post(`${API_BASE}/exhibitors/portal/${token}/staff`, data),
  getStaffBadgePdf: (token, staffId) => axios.get(`${API_BASE}/exhibitors/portal/${token}/staff/${staffId}/badge-pdf`, { responseType: 'blob' }),
  // Portal (exhibitor invitee management)
  getInvitees: (token) => axios.get(`${API_BASE}/exhibitors/portal/${token}/invitees`),
  createInvitee: (token, data) => axios.post(`${API_BASE}/exhibitors/portal/${token}/invitees`, data),
};

// ── Super Admin ───────────────────────────────────────────────
function saHeaders() {
  const token = localStorage.getItem('sa_token');
  return { Authorization: `Bearer ${token}` };
}

export const superadminAPI = {
  login: (email, password) => axios.post(`${API_BASE}/superadmin/login`, { email, password }),
  // Dashboard
  getDashboard: () => axios.get(`${API_BASE}/superadmin/dashboard`, { headers: saHeaders() }),
  // Clients
  getClients: () => axios.get(`${API_BASE}/superadmin/clients`, { headers: saHeaders() }),
  getClient: (clientId) => axios.get(`${API_BASE}/superadmin/clients/${clientId}`, { headers: saHeaders() }),
  updateClientStatus: (clientId, data) => axios.put(`${API_BASE}/superadmin/clients/${clientId}/status`, data, { headers: saHeaders() }),
  blockAI: (clientId) => axios.put(`${API_BASE}/superadmin/clients/${clientId}/block-ai`, {}, { headers: saHeaders() }),
  unblockAI: (clientId) => axios.put(`${API_BASE}/superadmin/clients/${clientId}/unblock-ai`, {}, { headers: saHeaders() }),
  deleteClient: (clientId) => axios.delete(`${API_BASE}/superadmin/clients/${clientId}`, { headers: saHeaders() }),
  // Notifications
  getNotifications: () => axios.get(`${API_BASE}/superadmin/notifications`, { headers: saHeaders() }),
  markAllNotificationsRead: () => axios.put(`${API_BASE}/superadmin/notifications/read-all`, {}, { headers: saHeaders() }),
  clearNotifications: () => axios.delete(`${API_BASE}/superadmin/notifications`, { headers: saHeaders() }),
  // Pricing
  getPricing: () => axios.get(`${API_BASE}/superadmin/pricing`, { headers: saHeaders() }),
  updatePricing: (data) => axios.put(`${API_BASE}/superadmin/pricing`, data, { headers: saHeaders() }),
  // Budgets
  getBudgets: () => axios.get(`${API_BASE}/superadmin/budgets`, { headers: saHeaders() }),
  getBudget: (budgetId) => axios.get(`${API_BASE}/superadmin/budgets/${budgetId}`, { headers: saHeaders() }),
  getBudgetPdf: (budgetId) => axios.get(`${API_BASE}/superadmin/budgets/${budgetId}/pdf`, { headers: saHeaders(), responseType: 'blob' }),
  recalculateBudget: (budgetId) => axios.put(`${API_BASE}/superadmin/budgets/${budgetId}/recalculate`, {}, { headers: saHeaders() }),
  // Contract Documents
  getDocuments: () => axios.get(`${API_BASE}/superadmin/contract-documents`, { headers: saHeaders() }),
  createDocument: (data) => axios.post(`${API_BASE}/superadmin/contract-documents`, data, { headers: saHeaders() }),
  activateDocument: (docId) => axios.put(`${API_BASE}/superadmin/contract-documents/${docId}/activate`, {}, { headers: saHeaders() }),
};

export default api;
